import { faPlus } from '@awesome.me/kit-a1e2571845/icons/classic/solid'
import { zodResolver } from '@hookform/resolvers/zod'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import FormLabel from '@mui/material/FormLabel'
import Input from '@mui/material/Input'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { createFileRoute } from '@tanstack/react-router'
import { Icon } from '@xweather/theme'
import i18next from 'i18next'
import { enqueueSnackbar } from 'notistack'
import { type ComponentProps, Fragment, type ReactNode, useState } from 'react'
import { Controller, type ControllerFieldState, FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { FormLayout2 } from '../../../components/FormLayout2.tsx'
import { I18n } from '../../../components/I18n.tsx'
import { IconButton } from '../../../components/IconButton.tsx'
import { useSubscription } from '../../../data/service.ts'
import { currentUserData } from '../../../data/userProfile.ts'
import { roleData, useCreateUsers, userData } from '../../../data/users.ts'
import { getSelectableRoles, RoleChip, RoleDescriptions } from './-roleUtils.tsx'

export const Route = createFileRoute('/_app/users/add')({
	loader: async ({ context }) => {
		await Promise.all([userData.ensure(context), roleData.ensure(), currentUserData.ensure()])
	},
	component: AddUsers,
})

function AddUsers() {
	const { data: users } = userData.use()
	const { data: subscription } = useSubscription()
	const navigate = Route.useNavigate()
	const [activeStep, setActiveStep] = useState(0)
	const { mutateAsync: createUsers, isPending } = useCreateUsers()
	const defaultUser = useDefaultUser()

	const form = useForm<UserFormValues>({
		resolver: zodResolver(createUsersFormSchema(users.map((u) => u.email.email))),
		defaultValues: { users: [defaultUser] },
	})

	const onSubmit = form.handleSubmit(
		async (data) =>
			await createUsers(
				data.users.map((u) => ({ email: u.email, name: u.name, roles: [u.role], subType: 'UIUser', type: 'Individual', blocked: false })),
				{
					onSuccess: async () => {
						enqueueSnackbar(<I18n i18nKey={($) => $.users.createSuccess} />, { variant: 'success' })
						await navigate({ to: '/users' })
					},
					onError: (e, users) => {
						if (e.message === 'USER_LIMIT_REACHED' && subscription) {
							enqueueSnackbar(<I18n i18nKey={($) => $.users.userLimitReached} values={{ maxUsers: subscription.maxUsers }} />, {
								variant: 'error',
							})
							return
						}
						enqueueSnackbar(<I18n i18nKey={($) => $.users.createFailure} />, { variant: 'error' })
						if (e.message === 'NON_UNIQUE_EMAIL') {
							setActiveStep(0)
							users.forEach((u, index) => {
								if (e.emails?.includes(u.email)) {
									form.setError(`users.${index}.email`, { type: 'custom', message: i18next.t(($) => $.users.couldNotAddEmail) })
								}
							})
						}
					},
				},
			),
		(_errors) => setActiveStep(0),
	)

	return (
		<FormProvider {...form}>
			<Box component='form' sx={{ display: 'contents' }} onSubmit={onSubmit}>
				<FormLayout2
					onBack={() => navigate({ to: '..' })}
					header={<I18n i18nKey={($) => $.users.add} />}
					sideBar={<FormLayout2.Steps steps={steps} activeStep={activeStep} />}
					bottomBar={
						<>
							<FormLayout2.Buttons
								onStepChange={setActiveStep}
								activeStep={activeStep}
								stepCount={steps.length}
								onCancel={() => navigate({ to: '..' })}
								onNext={form.trigger}
							/>
							{activeStep === steps.length - 1 && (
								<Button type='submit' disabled={isPending}>
									<I18n i18nKey={($) => $.users.sendInvites} />
									{isPending && <CircularProgress sx={{ ml: 1 }} />}
								</Button>
							)}
						</>
					}
				>
					{steps[activeStep]?.form}
				</FormLayout2>
			</Box>
		</FormProvider>
	)
}

const DetailsStep = () => {
	const { t } = useTranslation()
	const { data: roles } = roleData.use()
	const { control } = useFormContext<UserFormValues>()
	const { fields, append, remove } = useFieldArray({ control, name: 'users' })
	const defaultUser = useDefaultUser()
	const { data: currentUser } = currentUserData.use()
	const { data: users } = userData.use()
	const { data: subscription } = useSubscription()
	const canAddAnother = users.filter((u) => u.isUiUser).length + fields.length < subscription.maxUsers
	const selectableRoles = getSelectableRoles(currentUser, roles)
	return (
		<StepLayout
			top={
				<>
					<Typography>
						<I18n i18nKey={($) => $.users.detailsTitle} />
					</Typography>
					<RoleDescriptions />
				</>
			}
		>
			{fields.map((user, index) => (
				<Box key={user.id} sx={{ display: 'contents', label: { display: 'none' } }}>
					<Controller
						control={control}
						name={`users.${index}.name`}
						render={({ field, fieldState }) => (
							<TextFieldWithErrors {...field} label={<I18n i18nKey={($) => $.users.name} />} fieldState={fieldState} />
						)}
					/>
					<Controller
						control={control}
						name={`users.${index}.email`}
						render={({ field, fieldState }) => (
							<TextFieldWithErrors {...field} label={<I18n i18nKey={($) => $.users.email} />} fieldState={fieldState} />
						)}
					/>
					<Controller
						control={control}
						name={`users.${index}.role`}
						render={({ field, fieldState }) => (
							<TextFieldWithErrors {...field} label={<I18n i18nKey={($) => $.users.role} />} fieldState={fieldState} select>
								{selectableRoles.map((role) => (
									<MenuItem key={role.id} value={role.id}>
										<I18n i18nKey={($) => $.users.roles[role.name]} />
									</MenuItem>
								))}
							</TextFieldWithErrors>
						)}
					/>
					<IconButton icon='Trash' label={t(($) => $.delete)} color='error' size='small' onClick={() => remove(index)} />
				</Box>
			))}
			<Tooltip
				title={canAddAnother ? '' : <I18n i18nKey={($) => $.users.addAnotherLimitReached} values={{ maxUsers: subscription.maxUsers }} />}
			>
				<Box sx={{ gridColumn: 'span 3' }}>
					<Button
						variant='outlined'
						size='small'
						startIcon={<Icon icon={faPlus} />}
						disabled={!canAddAnother}
						onClick={() => append(defaultUser)}
						fullWidth
					>
						<I18n i18nKey={($) => $.users.addAnother} />
					</Button>
				</Box>
			</Tooltip>
		</StepLayout>
	)
}

const TextFieldWithErrors = ({ fieldState, ...props }: { fieldState: ControllerFieldState } & ComponentProps<typeof TextField>) => (
	<TextField {...props} size='small' error={!!fieldState.error} helperText={fieldState.error ? fieldState.error.message : null} />
)

const ReviewStep = () => {
	const { control } = useFormContext<UserFormValues>()
	const { fields } = useFieldArray({ control, name: 'users' })
	return (
		<StepLayout
			top={
				<>
					<Typography>
						<I18n i18nKey={($) => $.users.reviewTitle} />
					</Typography>
					<Typography variant='body2' color='textSecondary'>
						<I18n i18nKey={($) => $.users.reviewGuide} />
					</Typography>
				</>
			}
		>
			<Divider sx={{ gridColumn: 'span 4' }} />
			{fields.map((user) => (
				<Fragment key={user.id}>
					<Input readOnly value={user.name} />
					<Input readOnly value={user.email} />
					<Box>
						<RoleChipById roleId={user.role} />
					</Box>
					<Box />
					<Divider sx={{ gridColumn: 'span 4', mt: -1 }} />
				</Fragment>
			))}
		</StepLayout>
	)
}

const RoleChipById = ({ roleId }: { roleId: string }) => {
	const { data: roles } = roleData.use()
	const role = Object.values(roles).find((r) => r.id === roleId)
	if (!role) return null
	return <RoleChip role={role} />
}

const StepLayout = ({ top, children }: { top: ReactNode; children: ReactNode }) => (
	<Stack col divider={<Divider />} spacing={0}>
		<Stack col spacing={0} sx={{ p: 2, minH: 120 }}>
			{top}
		</Stack>
		<Box sx={{ p: 2, pb: 0, overflowY: 'auto', display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 32px', gap: 1 }}>
			<FormLabel component='div' required>
				<I18n i18nKey={($) => $.users.name} />
			</FormLabel>
			<FormLabel component='div' required>
				<I18n i18nKey={($) => $.users.email} />
			</FormLabel>
			<FormLabel component='div' required>
				<I18n i18nKey={($) => $.users.role} />
			</FormLabel>
			<FormLabel component='div' />
			{children}
		</Box>
	</Stack>
)

const steps = [
	{
		id: 'details',
		label: <I18n i18nKey={($) => $.users.formSteps.detailsLabel} />,
		description: <I18n i18nKey={($) => $.users.formSteps.detailsDescription} />,
		form: <DetailsStep />,
	},
	{
		id: 'review',
		label: <I18n i18nKey={($) => $.users.formSteps.reviewLabel} />,
		description: <I18n i18nKey={($) => $.users.formSteps.reviewDescription} />,
		form: <ReviewStep />,
	},
]

const useDefaultUser = () => {
	const { data: roles } = roleData.use()
	return { email: '', name: '', role: roles.user?.id ?? '' }
}

const UserSchema = z.object({
	email: z
		.email({ error: () => i18next.t(($) => $.users.invalidEmail) })
		.max(100, { error: () => i18next.t(($) => $.users.emailTooLong) })
		.transform((email) => email.toLowerCase()),
	name: z
		.string()
		.min(2, { error: () => i18next.t(($) => $.users.nameTooShort) })
		.max(100, { error: () => i18next.t(($) => $.users.nameTooLong) }),
	role: z.string(),
})
type UserFormValues = z.infer<ReturnType<typeof createUsersFormSchema>>

const createUsersFormSchema = (existingEmails: string[]) =>
	z.object({ users: z.array(UserSchema).min(1) }).superRefine((data, ctx) => {
		const emails = data.users.map((user) => user.email)
		const seen = new Set<string>()
		for (const [index, email] of emails.entries()) {
			if (existingEmails.includes(email)) {
				ctx.addIssue({ code: 'custom', path: ['users', index, 'email'], message: i18next.t(($) => $.users.emailInUse) })
			}
			if (seen.has(email)) {
				ctx.addIssue({ code: 'custom', path: ['users', index, 'email'], message: i18next.t(($) => $.users.duplicateEmails) })
			}
			seen.add(email)
		}
	})
